import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { Pill, StateCard } from '../../components/StateCard';
import { Empty, SiteHead, formatDate, ticketStatusLabel } from '../../components/site';
import type { ApiEnvelope } from '../../lib/types';

interface SupportOverview {
  categories: { category: string; count: number; articles: { slug: string; title: string; game_id: string | null }[] }[];
  article_count: number;
  games: { id: string; slug: string; name: string }[];
  open_incidents: { reference: string; title: string; severity: string; state: string }[];
  contact: { configured: boolean; is_local_sink: boolean; note: string | null };
}

/** P008 — support home. */
export function SupportHome() {
  const [searchParams, setSearchParams] = useSearchParams();
  const term = searchParams.get('q') ?? '';

  const overview = useResource<SupportOverview>(
    (signal) => api.public('/api/v1/support/overview', { signal }) as Promise<ApiEnvelope<SupportOverview>>,
    [],
    { emptyWhen: () => false },
  );

  const search = useResource<{ slug: string; title: string; category: string }[]>(
    (signal) => api.public('/api/v1/support/articles', { signal, query: { q: term, per_page: 20 } }) as Promise<ApiEnvelope<{ slug: string; title: string; category: string }[]>>,
    [term],
    // The screen shows its own "검색 결과가 없어요" with a way forward.
    { emptyWhen: () => false },
  );

  const data = overview.data;

  return (
    <>
      <SiteHead
        eyebrow="고객지원"
        title="도움이 필요할 때"
        lead="도움말을 찾아보고, 해결되지 않으면 문의해 주세요."
        meta={data && data.article_count > 0 ? <span>도움말 {data.article_count}건</span> : undefined}
      />

      <form
        className="form-card"
        style={{ marginBottom: 32 }}
        onSubmit={(event) => {
          event.preventDefault();
          const value = new FormData(event.currentTarget).get('q');
          const next = new URLSearchParams(searchParams);
          if (value) next.set('q', String(value));
          else next.delete('q');
          setSearchParams(next);
        }}
      >
        <label htmlFor="support-search">어떤 도움이 필요하세요?</label>
        <div className="search-row">
          <input id="support-search" name="q" type="search" defaultValue={term} placeholder="보상이 들어오지 않아요, 구매 내역이 보이지 않아요…" />
          <button type="submit" className="btn btn--primary">검색</button>
        </div>
      </form>

      {term ? (
        <StateCard state={search.state} error={search.error} onRetry={search.reload}>
          {(search.data?.length ?? 0) === 0 ? (
            <Empty
              title="검색 결과가 없어요"
              body="다른 검색어로 찾아보거나 문의를 남겨 주세요."
              action={<Link className="btn btn--primary" to="/support/new">문의 남기기</Link>}
            />
          ) : (
            <div className="row-list">
              {search.data!.map((article) => (
                <Link key={article.slug} to={`/support/articles/${article.slug}`} className="row">
                  <div className="row__main"><h3 className="row__title">{article.title}</h3></div>
                  <div className="row__aside"><span>{article.category}</span></div>
                </Link>
              ))}
            </div>
          )}
        </StateCard>
      ) : (
        <StateCard state={overview.state} error={overview.error} onRetry={overview.reload}>
          {data && (
            <>
              {data.open_incidents.length > 0 && (
                <div className="banner" style={{ marginBottom: 28 }}>
                  <span aria-hidden="true">!</span>
                  <div>
                    진행 중인 장애:{' '}
                    {data.open_incidents.map((incident) => (
                      <Link key={incident.reference} to={`/status/incidents/${incident.reference}`} style={{ marginRight: 10 }}>
                        {incident.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {data.categories.length === 0 ? (
                <Empty
                  title="아직 도움말이 없어요"
                  body="궁금한 내용을 문의로 남겨 주세요."
                  action={<Link className="btn btn--primary" to="/support/new">문의 남기기</Link>}
                />
              ) : (
                <div className="support-grid">
                  {data.categories.map((group) => (
                    <section key={group.category} className="support-card">
                      <h3>{group.category}</h3>
                      <p className="support-card__count">{group.count}건</p>
                      <ul>
                        {group.articles.map((article) => (
                          <li key={article.slug}>
                            <Link to={`/support/articles/${article.slug}`}>{article.title}</Link>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              )}

              <section style={{ marginTop: 44 }}>
                <h2 className="section__title" style={{ marginBottom: 18 }}>문의 창구</h2>

                {data.contact.configured ? (
                  <div className="card-grid">
                    <Link to="/support/new" className="card" style={{ color: 'inherit' }}>
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>문의 남기기</div>
                      <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                        게임·플랫폼·버전을 함께 알려 주세요. 접수 메일의 링크에서 답변을 확인할 수 있어요.
                      </div>
                    </Link>
                    <a href="mailto:games@askrs.com" className="card" style={{ color: 'inherit' }}>
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>games@askrs.com</div>
                      <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                        이메일로 문의하면 접수번호가 발급되지 않아요.
                      </div>
                    </a>
                    <Link to="/status" className="card" style={{ color: 'inherit' }}>
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>서비스 상태</div>
                      <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                        점검·장애 현황
                      </div>
                    </Link>
                  </div>
                ) : (
                  <div className="banner">
                    <span aria-hidden="true">!</span>
                    <div>{data.contact.note}</div>
                  </div>
                )}
              </section>

              <div className="chips" style={{ marginTop: 32 }}>
                <Link className="chip" to="/legal/terms">이용약관</Link>
                <Link className="chip" to="/legal/privacy">개인정보 처리방침</Link>
                <Link className="chip" to="/privacy/requests">내 데이터 요청</Link>
              </div>
            </>
          )}
        </StateCard>
      )}
    </>
  );
}

interface Article {
  slug: string; title: string; body: string; category: string; locale: string;
  platforms: string[]; applies_to_builds: string | null; revision: number; updated_at: string | null;
}

/** P009 — help article. */
export function SupportArticle() {
  const { slug } = useParams();

  const article = useResource<Article>(
    (signal) => api.public(`/api/v1/support/articles/${slug}`, { signal }) as Promise<ApiEnvelope<Article>>,
    [slug],
    { emptyWhen: () => false },
  );

  const data = article.data;

  return (
    <StateCard state={article.state} error={article.error} onRetry={article.reload}>
      {data && (
        <article className="article">
          <nav className="breadcrumb" style={{ marginBottom: 20 }}>
            <Link to="/support">고객지원</Link> / {data.category}
          </nav>
          <h1 className="site-head__title">{data.title}</h1>
          <div className="article__meta">
            <Pill tone="neutral">{data.category}</Pill>
            <span>업데이트 {formatDate(data.updated_at)}</span>
            <span>revision {data.revision}</span>
          </div>

          {data.applies_to_builds && (
            <div className="article__notice">
              빌드 {data.applies_to_builds} 기준 · 다른 버전의 화면과 차이 가능
            </div>
          )}

          <div className="article__body">{data.body}</div>

          <div className="card" style={{ marginTop: 40 }}>
            <p style={{ margin: '0 0 12px' }}>이걸로 해결되지 않았다면</p>
            <Link className="btn btn--primary" to={`/support/new?article=${data.slug}`}>문의 남기기</Link>
          </div>
        </article>
      )}
    </StateCard>
  );
}

const CATEGORIES = [
  { value: 'reward', label: '보상 미지급' },
  { value: 'purchase', label: '결제·구매' },
  { value: 'account', label: '계정·로그인' },
  { value: 'bug', label: '오류·버그' },
  { value: 'other', label: '기타' },
];

const PLATFORMS = [
  { value: '', label: '선택 안 함' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'roblox', label: 'Roblox' },
  { value: 'steam', label: 'Steam' },
  { value: 'pc', label: 'PC' },
  { value: 'web', label: 'Web' },
];

/** P010 — ticket submission. */
export function SupportNew() {
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [result, setResult] = useState<{ reference: string; note: string } | null>(null);

  const overview = useResource<SupportOverview>(
    (signal) => api.public('/api/v1/support/overview', { signal }) as Promise<ApiEnvelope<SupportOverview>>,
    [],
    { emptyWhen: () => false },
  );

  const configured = overview.data?.contact.configured ?? false;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSubmitting(true);
    setError(null);

    try {
      const response = await api.public('/api/v1/support/tickets', {
        method: 'POST',
        body: {
          game: form.get('game') || null,
          platform: form.get('platform') || null,
          app_version: form.get('app_version') || null,
          category: form.get('category'),
          subject: form.get('subject'),
          body: [form.get('body'), searchParams.get('article') ? `\n\n(참조 도움말: ${searchParams.get('article')})` : ''].join(''),
          contact_email: form.get('contact_email'),
          consent: form.get('consent') === 'on',
        },
      }) as ApiEnvelope<{ reference: string; note: string }>;

      setResult(response.data);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause : new ApiError(0, 'UNKNOWN', '전송 실패', '-'));
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <>
        <SiteHead eyebrow="고객지원" title="문의 접수 완료" lead={`접수번호 ${result.reference}`} />
        <div className="card">
          <p style={{ margin: '0 0 16px', lineHeight: 1.8 }}>{result.note}</p>
          <Link className="btn" to="/support">고객지원으로</Link>
        </div>
      </>
    );
  }

  return (
    <>
      <SiteHead
        eyebrow="고객지원"
        title="문의 남기기"
        lead="비밀번호·결제 정보 입력 금지"
      />

      {!configured && overview.loaded && (
        <div className="banner" style={{ marginBottom: 24 }}>
          <span aria-hidden="true">!</span>
          <div>{overview.data?.contact.note ?? '문의 접수 불가 · 수신처 미설정'}</div>
        </div>
      )}

      {error && (
        <div className="banner" style={{ marginBottom: 24 }}>
          <span aria-hidden="true">!</span>
          <div>
            {error.message}
            {Object.keys(error.fields).length > 0 && (
              <ul style={{ margin: '8px 0 0 16px' }}>
                {Object.entries(error.fields).map(([field, reason]) => (
                  <li key={field}>{field}: {Array.isArray(reason) ? reason[0] : reason}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <form className="form-card" onSubmit={submit}>
        <div className="form-grid">
          <div>
            <label htmlFor="ticket-game">게임</label>
            <select id="ticket-game" name="game" defaultValue="">
              <option value="">해당 없음</option>
              {(overview.data?.games ?? []).map((game) => (
                <option key={game.id} value={game.slug}>{game.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="ticket-platform">플랫폼</label>
            <select id="ticket-platform" name="platform" defaultValue="">
              {PLATFORMS.map((platform) => (
                <option key={platform.value} value={platform.value}>{platform.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="ticket-category">문의 유형</label>
            <select id="ticket-category" name="category" defaultValue="reward" required>
              {CATEGORIES.map((category) => (
                <option key={category.value} value={category.value}>{category.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="ticket-version">앱 버전</label>
            <input id="ticket-version" name="app_version" placeholder="예: 1.4.2" />
          </div>
        </div>

        <label htmlFor="ticket-subject">제목</label>
        <input id="ticket-subject" name="subject" required maxLength={200} />

        <label htmlFor="ticket-body">내용</label>
        <textarea id="ticket-body" name="body" required rows={8} maxLength={5000} placeholder="언제, 어떤 화면에서, 무엇이 일어났는지" />

        <label htmlFor="ticket-email">회신받을 이메일</label>
        <input id="ticket-email" name="contact_email" type="email" required />

        <label className="form-check">
          <input type="checkbox" name="consent" required />
          <span>문의 처리에 입력한 정보를 쓰는 데 동의</span>
        </label>

        <button type="submit" className="btn btn--primary" disabled={submitting || !configured}>
          {submitting ? '전송 중…' : '문의 보내기'}
        </button>
      </form>
    </>
  );
}

interface TicketPayload {
  reference: string; subject: string; status: string; last_activity_at: string | null;
  messages: { author: string; body: string; created_at: string }[];
}

/** P011 — ticket thread, reachable only with the emailed token. */
export function SupportTicket() {
  const { ticketId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [requested, setRequested] = useState(false);

  const ticket = useResource<TicketPayload>(
    (signal) => api.public(`/api/v1/support/tickets/${ticketId}`, { signal, query: { token } }) as Promise<ApiEnvelope<TicketPayload>>,
    [ticketId, token],
    { emptyWhen: () => false },
  );

  async function requestLink(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await api.public('/api/v1/support/ticket-access', {
      method: 'POST',
      body: { reference: ticketId, contact_email: form.get('contact_email') },
    });
    setRequested(true);
  }

  async function send() {
    if (!reply.trim()) return;
    setSending(true);
    setReplyError(null);
    try {
      await api.public(`/api/v1/support/tickets/${ticketId}/messages`, {
        method: 'POST',
        body: { token, body: reply },
      });
      setReply('');
      ticket.reload();
    } catch (cause) {
      setReplyError(cause instanceof ApiError ? cause.message : '답변 전송 실패');
    } finally {
      setSending(false);
    }
  }

  if (!token) {
    return (
      <>
        <SiteHead eyebrow="고객지원" title="문의 확인" lead="메일로 받은 확인 링크 필요 · 접수번호로 조회 불가" />
        {requested ? (
          <div className="card">등록된 문의이면 확인 링크 발송 · 메일함 확인</div>
        ) : (
          <form className="form-card" onSubmit={requestLink}>
            <label htmlFor="ticket-ref">접수번호</label>
            <input id="ticket-ref" value={ticketId ?? ''} readOnly />
            <label htmlFor="access-email">접수 시 입력한 이메일</label>
            <input id="access-email" name="contact_email" type="email" required />
            <button type="submit" className="btn btn--primary">확인 링크 다시 받기</button>
          </form>
        )}
      </>
    );
  }

  const data = ticket.data;

  return (
    <StateCard state={ticket.state} error={ticket.error} onRetry={ticket.reload}>
      {data && (
        <>
          <SiteHead
            eyebrow={`문의 ${data.reference}`}
            title={data.subject}
            meta={<Pill tone={data.status === 'RESOLVED' ? 'ok' : 'info'}>{ticketStatusLabel(data.status)}</Pill>}
          />

          <div className="timeline">
            {data.messages.map((message, index) => (
              <div key={index} className="timeline__item">
                <div className="timeline__meta">
                  <strong>{message.author === 'user' ? '내 문의' : '고객지원'}</strong>
                  <span>{formatDate(message.created_at, true)}</span>
                </div>
                <p className="timeline__body">{message.body}</p>
              </div>
            ))}
          </div>

          <div className="form-card" style={{ marginTop: 28 }}>
            <label htmlFor="ticket-reply">답변 남기기</label>
            <textarea
              id="ticket-reply"
              rows={5}
              maxLength={5000}
              value={reply}
              onChange={(event) => setReply(event.target.value)}
            />
            {replyError && <p style={{ color: 'var(--danger)', margin: '8px 0 0' }}>{replyError}</p>}
            <button type="button" className="btn btn--primary" disabled={sending || !reply.trim()} onClick={send}>
              {sending ? '전송 중…' : '답변 보내기'}
            </button>
          </div>
        </>
      )}
    </StateCard>
  );
}
